
import { useEffect, useState, useRef } from "react";
import { getAuth } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { db } from "../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import "../webapp.css";
import { AlertStack } from '../functions/ui';
import { addRippleEffect } from '../functions/classes';import { NoResultBox } from '../functions/ui';
import { Divider } from 'antd';
import { PiClipboardText, PiCaretLeft, PiHourglassMediumFill, PiClockUserFill, PiToolbox } from 'react-icons/pi';
import { BsCalendar2Date } from "react-icons/bs";
import { GrStatusGood } from "react-icons/gr";
import { SlLocationPin } from "react-icons/sl";
import { CiBookmarkCheck } from "react-icons/ci";
import { GiSkills } from "react-icons/gi";
import { TbClockHour10 } from "react-icons/tb";

function ViewShifts() {
  
  const location = useLocation();
  const staff = location?.state?.staff || {}; // Get staff info from location.state (passed from staff.js context menu navigation)
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, shift: null });
  const menuRef = useRef(null);

  const handleBack = () => {
    window.history.back();
  };

  useEffect(() => {
    if (!staff.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Get current user UID (companyUID)
    const auth = getAuth();
    const currentUID = auth.currentUser?.uid;
    if (!currentUID) {
      setShifts([]);
      setLoading(false);
      return;
    }
    // Query shifts accepted by staff and posted by current user
    const q = query(
      collection(db, "shifts"),
      where("acceptedBy", "==", staff.id),
      where("companyID", "==", currentUID)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const shiftList = [];
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        shiftList.push({ id: docSnap.id, ...data });
      }
      setShifts(shiftList);
      setLoading(false);
    }, (error) => {
      setShifts([]);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [staff.id]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (contextMenu.visible && menuRef.current && !menuRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    }
    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  useEffect(() => {
    // Highlight the staff sidebar button when this view is mounted
    const staffBtn = document.querySelector('.sidebar a[href="/dashboard/staff"]');
    if (staffBtn) {
      staffBtn.classList.add('active');
    }
    return () => {
      if (staffBtn) staffBtn.classList.remove('active');
    };
  }, []);

  // Helper: Get full address for shift location
  const getFullAddress = (shift) => {
    const parts = [
      shift.streetNumber,
      shift.streetName,
      shift.city,
      shift.province,
      shift.country,
      shift.postalCode
    ];
    return parts.filter(Boolean).join(", ");
  };

  // Helper: Status logic
  const getStatus = (shift) => {
    switch (shift.status) {
      case 'pending':
        return { label: "Pending", background: "#bdbdbd" }; // grey
      case 'accepted':
        return { label: "Accepted", background: "#a0522d" }; // brown
      case 'in-progress':
        return { label: "In-progress", background: "#fbbf24" }; // yellow
      case 'completed':
        return { label: "Completed", background: "#10b981" }; // green
      default:
        return { label: "TBD", background: "#6b7280" };
    }
  };

  const handleCopyShiftID = async (shift) => {
    try {
      await navigator.clipboard.writeText(shift.id);
      setAlerts(prev => [
        ...prev,
        { id: Date.now() + Math.random(), message: 'Shift ID successfully copied', type: 'success' },
      ]);
    } catch {
      setAlerts(prev => [
        ...prev,
        { id: Date.now() + Math.random(), message: 'Failed to copy Shift ID', type: 'error' },
      ]);
    }
    closeContextMenu();
  };

  const closeContextMenu = () => setContextMenu({ ...contextMenu, visible: false });

  return (
      <div className="dashboard-box">
        <button className="back-btn" onClick={handleBack}>
          <PiCaretLeft size={20} />
        </button>
        <h2>View Staff Shift</h2>
        <p className='small-text'>Displaying shifts for {staff.firstName} {staff.lastName}</p>
        <Divider className='dash-divider'/>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
          <div className="second-table-wrapper view-table-wrapper">
            <div className="table-wrapper view-table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th><div><CiBookmarkCheck className="table-icon icon-grey" /> Time accepted</div></th>
                  <th><div><GrStatusGood className="table-icon icon-blue" /> Status</div></th>
                  <th><div><PiHourglassMediumFill className="table-icon icon-grey" /> Hours done</div></th>
                  <th><div><PiClockUserFill className="table-icon icon-yellow" /> Time clockedin</div></th>
                  <th><div><TbClockHour10 className="table-icon icon-blue" /> Time clockedout</div></th>
                  <th><div><SlLocationPin className="table-icon icon-grey" /> Shift location</div></th>
                  <th><div><BsCalendar2Date className="table-icon icon-blue" /> Date created</div></th>
                  <th><div><GiSkills className="table-icon icon-yellow" /> Staff type</div></th>
                  <th><div><PiToolbox className="table-icon icon-grey" /> Skill</div></th>
                </tr>
              </thead>
            </table>
            {shifts.length > 0 ? (
              <div className="tbody-overflow">
                <table className="table">
                  <tbody>
                    {loading ? (
                      <tr><td>Loading...</td></tr>
                    ) : (
                      shifts.map((shift) => (
                        <tr key={shift.id} onContextMenu={e => {
                          e.preventDefault();
                          setContextMenu({ visible: true, x: e.clientX, y: e.clientY, shift });
                        }}>
                          <td>{shift.acceptedAt ? new Date(shift.acceptedAt).toLocaleString() : "-"}</td>
                          {(() => {
                            const status = getStatus(shift);
                            return (
                              <td>
                                <span style={{
                                  color: '#fff',
                                  background: status.background,
                                  borderRadius: 8,
                                  padding: "2px 8px",
                                }}>{status.label}</span>
                              </td>
                            );
                          })()}
                          <td>{shift.timedone ? shift.timedone : "-"}</td>
                          <td>{shift.clockedinAt ? new Date(shift.clockedinAt).toLocaleString() : "Not Clocked In"}</td>
                          <td>{shift.clockedoutAt ? new Date(shift.clockedoutAt).toLocaleString() : "Not Clocked Out"}</td>
                          <td>{getFullAddress(shift) || "-"}</td>
                          <td>{shift.dateCreated ? new Date(shift.dateCreated).toLocaleString() : "-"}</td>
                          <td>{shift.staffType || "-"}</td>
                          <td>{shift.skill || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="error-div">
                <NoResultBox />
              </div>
            )}
          </div>
          </div>
          {contextMenu.visible && (
            <div className="menu-box" style={{top: contextMenu.y, left: contextMenu.x, position: 'fixed'}} ref={menuRef}>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); handleCopyShiftID(contextMenu.shift); }}>
                <PiClipboardText className="profile-menu-icon" /><span>Copy ShiftID</span></div>
            </div>
          )}
          <AlertStack
            alerts={alerts}
            onAlertClose={id => setAlerts(alerts => alerts.filter(a => a.id !== id))}
          />
          </>
        )}
      </div>
  );
}

export default ViewShifts;

//the functions in view.js that are similar to shift-info.js were created independently because 
//view.js will be deleted to develop shift-info.js later on.